/* ============================================================
   MAIN — wires the modules together. Read this first.
   ============================================================ */

import { initHeader }       from "./header.js";
import { initSmooth }       from "./smooth.js";
import { splitChars, tintRange } from "./split.js";
import { createHeroCanvas } from "./heroCanvas.js";
import { createReel }       from "./reel.js";
import { createFlow }       from "./flow.js";
import { initVideo, initSound } from "./video.js";
import { initAudio }        from "./audio.js";
import { initPeek, initCharge, initFaq, initPartners, initCartBar } from "./homeSections.js";
import * as scenes          from "./scenes.js";
import { watchLayout }      from "./st-refresh.js";

/* The header goes up before anything touches GSAP. It is navigation
   now, not decoration — it holds the only route to the cart — so it
   must not be downstream of a CDN. Everything below this line is. */
initHeader();

/* ---------- the sections added in the page re-order ----------
   All four are in homeSections.js and all four are CONTENT, not
   decoration — the Smart Mop card carries the price and the Add to
   cart button, the FAQ answers questions, the partner band is proof.

   ⚠ THEY ARE HERE, ABOVE THE GSAP GUARD, AND THAT PLACEMENT IS THE
   WHOLE POINT. A few lines below, this file throws if GSAP did not
   arrive. Anything after that line does not run. Putting the only
   Add-to-cart button on the home page behind an animation library
   would mean a missing script costs a sale, which is exactly the
   failure the guard was written to prevent.

   initPeek is the only async one — it reads the server-rendered
   catalogue. Deliberately NOT awaited: nothing below depends on the
   card existing, and a render must not hold up the hero.

   ScrollTrigger.refresh() when it lands, and it is optional-chained
   because this runs before the guard: on a page with no GSAP there
   is nothing to refresh and nothing to fail. */
initCharge();
initFaq();
initPartners();
initCartBar();
initPeek().then(() => window.ScrollTrigger?.refresh());

/* ============================================================
   ⚠ THE LOADER IS GONE, AND WITH IT THE WHOLE FAILURE MODE IT USED
   TO CREATE.

   #load was a full-screen panel over the home page that only GSAP
   could take down — loader.js ended on a .set("#load",
   {display:"none"}). So any failure before that line left the panel
   up over a page that had otherwise finished loading: a blocked CDN,
   a slow network, an app script throwing first. That is the blank
   screen people reported, and the three belts that used to sit here
   — a 6s failsafe timer, an `error` listener and an
   `unhandledrejection` listener, all of which did nothing but hide
   #load — existed solely to catch it.

   There is no panel now. Nothing covers the page at any point, so
   there is nothing to dismiss and nothing to dismiss it from. All
   three belts came out with it; keeping them would have been three
   listeners hunting for an element that is not in the document.

   The intro itself is unchanged — the hero headline and card still
   animate in, they simply start straight away instead of waiting
   ~2.9s for a panel to lift. See the timeline's `delay` below.

   The loader has not been deleted, only unhooked. Every piece of it
   — the module, the CSS, the markup, and the exact edits needed to
   put it back — is in ../removed-loader/ beside the theme folder.
   ============================================================ */

const { gsap, ScrollTrigger } = window;

if(!gsap || !ScrollTrigger){
  console.warn("[Caspian] GSAP did not load — showing the page without its animation.");
  /* This is a soft landing now rather than a rescue. Nothing below
     has run, so nothing has been hidden: the hero headline is
     animated in FROM an off state further down and that state has
     not been applied. The page is already fully visible, the header
     is up, and the four content sections above this line — the Smart
     Mop card with its price and Add to cart, the charge callout, the
     FAQ and the partner bands — have all already rendered. */
  throw new Error("[Caspian] GSAP unavailable; scroll animation skipped.");
}

gsap.registerPlugin(ScrollTrigger);
gsap.config({ force3D: true });
ScrollTrigger.config({ ignoreMobileResize: true });

const REDUCED = matchMedia("(prefers-reduced-motion:reduce)").matches;

/* ---------- hero + reel media ----------
   Both are real footage now. heroCanvas.js and the canvas frame
   path in reel.js are kept for the case where they go back to
   being generated rather than filmed. */
const heroCanvas = document.getElementById("hero-canvas");
if(heroCanvas) createHeroCanvas(heroCanvas);

/* ---------- video ----------
   Loading, playback and every reason a phone might refuse to start a
   clip now live in modules/video.js. Read the header there before
   changing anything about how the videos behave.

   The showreel is a mobile-only beat and CSS decides that — see the
   min-width rule on .reel. A display:none section never intersects, so
   it is never fetched on desktop, with no breakpoint logic here.

   initHero() is NOT called any more, and the import is gone with it.
   It existed for the one clip that carried a real src and had to
   start fetching without waiting for script — the hero video. The
   hero is a headline now; that footage is the Shark Tank feature
   further down the page, and it lazy-loads on its data-src like
   every other clip. initHero is still exported from video.js for
   whenever a page has an above-the-fold clip again. */
initVideo();
initSound();

/* ---------- testimonial audio ----------
   Independent of the clips above and of GSAP: it is one <audio>
   element with its own transport and its own waveform. It never
   starts on its own, so there is no autoplay story to tell here —
   see the header in modules/audio.js. */
initAudio();

const reelCanvas = document.getElementById("reel-canvas");
const reel = reelCanvas ? createReel(reelCanvas) : null;

/* ---------- split the hero headline ----------
   ⚠ THERE IS NO .hero-tag ON THE HOME PAGE ANY MORE. The hero was
   the single line "Now mop your house in just 5 mins!" and it has
   been removed on request; sections/premium-hero.liquid opens the
   page instead, and it brings its own entrance.

   This block is GUARDED rather than deleted, and the guard is
   load-bearing: `tag.querySelector` on a null is a TypeError, and it
   would have thrown here — above every scroll beat in this file, on
   every page load. The whole page would have stopped animating.

   `chars` stays an array either way, so the timeline below tweens an
   empty selection and does nothing, which is what it should do.

   Put a .hero-tag back in the markup and this starts working again
   with no change here. */
const tag = document.querySelector(".hero-tag");
let chars = [];
if(tag){
  const accent = tag.querySelector("em")?.textContent.trim() ?? "";
  const before = tag.textContent.indexOf(accent);
  chars = splitChars(tag);
  if(accent && before > -1){
    // count non-space chars before the accent run
    const skip = tag.textContent.slice(0, before).replace(/\s/g, "").length;
    tintRange(chars, skip, accent.replace(/\s/g, "").length, "var(--cyan)");
  }
}

/* ---------- chrome ---------- */
initSmooth(gsap, ScrollTrigger);
// Magnetic pull on buttons is deliberately off — the .btn hover
// carries the interaction on its own. cursor.js is kept for later.
// The initLoader(gsap) call was here. See the note at the top of this
// file, and ../removed-loader/ for the code.
createFlow(gsap, ScrollTrigger);

/* ---------- hero intro ---------- */
/* The .hero-mark tween that used to open this timeline is gone with
   the element: the Caspian lockup is in the sticky header now, and
   the header must not be animated in — it is navigation, and it goes
   up before anything here runs.

   ⚠ THE DELAY WAS 2.9 SECONDS AND IS NOW ESSENTIALLY NOTHING. That
   2.9 was not a design choice about the hero — it was the length of
   the loader, measured. The hero was never chained to the loader's
   timeline, only timed to start as the panel finished lifting, so
   with the panel gone the delay was 2.9 seconds of a visitor looking
   at a hero that had been deliberately hidden from them.

   .12 is left so the tween starts on the frame after layout rather
   than during it. If the loader is ever put back, this goes back to
   2.9 — ../removed-loader/README.md says so too.

   ⚠ THE .hero-card TWEEN HAS ALSO GONE, with the element. The hero
   video was cut back to its headline; the sale poster below takes
   its place and rises on its own trigger, in scenes.posterIn,
   because it is below the headline rather than beside it.

   What is left is the headline, letter by letter. */
gsap.timeline({ delay: REDUCED ? 0 : .12 })
  // fromTo with y:0 — GSAP reads a computed transform as a pixel
  // matrix, so a percentage start state has to zero both yPercent
  // and y or the characters stay pushed down.
  .fromTo(chars, { yPercent: 102, y: 0 },
                 { yPercent: 0, y: 0, duration: 1, stagger: .012, ease: "expo.out" }, .12);

/* ---------- scroll beats ---------- */
// scenes.heroRecede — it scrubbed a slow push-in on .hero-card, and
// that element is gone with the hero video. Kept in scenes.js for
// whenever the hero carries a visual again.
scenes.posterIn(gsap);
scenes.crossingLines(gsap, ScrollTrigger);
// scenes.ribbons — the product panel it animated is off the page; the
// how-to-use section stands in its place and brings its own beat.
// No ScrollTrigger — the band runs on its own clock now, see scenes.js
scenes.marquee(gsap);
if(reel) scenes.reelScene(gsap, ScrollTrigger, reel);
// scenes.horizontalWork — the three step cards it slid past each other
// are now one clip, so there is nothing left to scroll sideways. Kept
// in scenes.js for the case where the steps go back to being separate.
scenes.howSteps(gsap);
scenes.parallax(gsap, ScrollTrigger);
scenes.stackingCards(gsap);
scenes.counters(gsap, ScrollTrigger);
scenes.footerCta(gsap);
// Why smartmop, the Title + video / Title + image bands — the same
// beat they get on the product page. Gated on its own markup inside.
if(!REDUCED) scenes.storyBeats(gsap, ScrollTrigger);

/* Re-measure whenever the page can have changed height — late
   images, webfonts swapping in, the address bar collapsing. Was a
   single refresh on `load`; see the header in st-refresh.js for why
   that is not enough once every asset is a cross-origin request. */
watchLayout(ScrollTrigger);
